/* RESPONSIBLE TEAM: team-frontend-tech */

import Modifier from 'ember-modifier';
import { inject as service } from '@ember/service';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import { registerDestructor } from '@ember/destroyable';
import { loadStylesheet } from 'embercom/lib/dom-utils';
import { getTeammateAppUrls } from 'embercom/lib/react-islands';
import ENV from 'embercom/config/environment';

interface SetupReactPortalShadowDomSignature {
  Element: Element;
  Args: {
    Named: Record<string, never>;
  };
}

/**
 * Modifier that sets up a Shadow DOM with React styles for portaling React content.
 * Similar to render-react-component but only creates the Shadow DOM infrastructure,
 * not rendering React components directly. React content is portaled in separately.
 */
class SetupReactPortalShadowDom extends Modifier<SetupReactPortalShadowDomSignature> {
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  hasShadowRoot = false;

  constructor(owner: any, args: any) {
    super(owner, args);
    registerDestructor(this, this.cleanup);
  }

  modify(element: Element) {
    this.setupShadowDom(element);
  }

  cleanup = () => {
    // No cleanup needed - shadow DOM and styles remain
  };

  async setupShadowDom(element: Element) {
    if (this.hasShadowRoot) {
      return;
    }

    if (ENV.environment === 'test') {
      return;
    }

    let { cssUrl } = getTeammateAppUrls();

    // Create shadow root
    let shadowRoot = element.attachShadow({ mode: 'open' });
    this.hasShadowRoot = true;

    // Create portal target div inside shadow DOM FIRST (before async stylesheet load)
    // This ensures React can find it immediately, even if styles are still loading
    let portalTarget = document.createElement('div');
    portalTarget.id = 'portal-target';
    portalTarget.style.position = 'relative';
    portalTarget.classList.add('overflow-hidden', 'rounded-xl', 'h-full');
    portalTarget.style.visibility = 'hidden'; // Hide until styles load to prevent FOUC
    shadowRoot.appendChild(portalTarget);

    // Load stylesheet into shadow DOM (async)
    await loadStylesheet(cssUrl, shadowRoot);

    // Show content now that styles are loaded
    portalTarget.style.visibility = 'visible';
  }
}

export default SetupReactPortalShadowDom;

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'setup-react-portal-shadow-dom': typeof SetupReactPortalShadowDom;
  }
}
